<template>
  <FlexCol gap-3>
    <Flex gap-3 justify-end>
      <InputZoom database="itemWarehouse" item-value="id" item-text="name" placeholder="Pesquisar" :item-autocomplete="['code', 'name']" class="w-[400px]" clearable v-model="selectedItem">
        <template #selected="{ item }"> {{ item.code }} {{ item.name }} </template>
      </InputZoom>

      <Button label="Exportar" primary icon="fa-solid fa-arrow-up-from-bracket" @click="exportStock()" />
    </Flex>
    <TheLoader v-if="loader" />
    <TableFull :headers="stockHeaders" :items="itemWarehouse" v-model="control" @doubleClick="handleDoubleClick" dense>
      <template v-slot:itemCode="{ item }">{{ item.itemCode }} </template>
      <template v-slot:total="{ item }">{{ formatMoneyS(item.total) }} </template>
      <template v-slot:actions="{ item }">
        <Flex gap-3>
          <TheToolTip label="Exibir histórico">
            <TableIconButton icon="fa-eye" @click="viewHistory(item)" />
          </TheToolTip>
        </Flex>
      </template>
    </TableFull>
  </FlexCol>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue'
import { get, getFile } from '@/crud'
import { useRouter } from 'vue-router'
import { useFormat } from '@/composables/useFormat'

const router = useRouter()
const { formatMoneyS } = useFormat()

// Props
const props = defineProps({
  id: [String, Number],
  warehouseName: String,
  type: String,
  card: Boolean,
})
//  Varibles
const api = 'itemWarehouse'

const control = ref({
  total: 0,
  limit: 50,
  pages: 0,
  page: 1,
  order: [{ direction: 'ASC', column: 'code', table: 'item', name: 'Código', raw: 'cast(item.code as SIGNED)' }],
})

const loader = ref(false)

const itemWarehouse = ref([])
const selectedItem = ref(null)

const stockHeaders = computed(() => {
  // No card lateral o espaço é menor: colunas mais estreitas para caber sem espremer código/material
  const codeHeader = {
    title: 'Código',
    field: 'itemCode',
    table: 'item',
    orderField: 'code',
    raw: 'cast(item.code as SIGNED)',
    width: props.card ? '80px' : '110px',
  }

  const nameHeader = {
    title: 'Material',
    field: 'itemName',
    table: 'item',
    orderField: 'name',
    itemClass: '!text-wrap',
    width: props.card ? '240px' : '360px',
  }

  const measureWidth = props.card ? '80px' : '120px'
  const numberWidth = props.card ? '90px' : '120px'
  const moneyWidth = props.card ? '110px' : '140px'

  // Se for depósito tipo 'physic' (físico), mostra apenas colunas básicas
  if (props.type === 'physic') {
    return [
      codeHeader,
      nameHeader,
      {
        title: 'Und Medida',
        field: 'criterionOfMeasurementSymbol',
        table: 'criterionOfMeasurement',
        orderField: 'symbol',
        width: measureWidth,
      },
      { title: 'Saldo', field: 'quantity', width: numberWidth },
      { title: 'Preço Médio', field: 'avgPrice', table: 'item', orderField: 'avgPrice', width: moneyWidth, format: formatMoneyS },
      { title: 'Último Preço', field: 'lastPrice', width: moneyWidth, format: formatMoneyS, disableOrder: true },
    ]
  }

  if (props.type === 'team' && props.card) {
    return [
      codeHeader,
      nameHeader,
      { title: 'Saldo', field: 'quantity', width: numberWidth },
      { title: 'Ações', field: 'actions', width: '90px', headerClass: 'justify-center', disableOrder: true },
    ]
  }

  // Para outros tipos de depósito (team, vehicles), mostra todas as colunas
  return [
    codeHeader,
    nameHeader,
    {
      title: 'Und Medida',
      field: 'criterionOfMeasurementSymbol',
      table: 'criterionOfMeasurement',
      orderField: 'symbol',
      width: measureWidth,
    },
    { title: 'Fornecido', field: 'supplied', width: numberWidth, disableOrder: true },
    { title: 'Aplicado', field: 'applied', width: numberWidth, disableOrder: true },
    { title: 'Saldo', field: 'quantity', width: numberWidth },
    { title: 'Preço Médio', field: 'avgPrice', table: 'item', orderField: 'avgPrice', width: moneyWidth, format: formatMoneyS },
    { title: 'Último Preço', field: 'lastPrice', width: moneyWidth, format: formatMoneyS, disableOrder: true },
    { title: 'Total', field: 'total', width: numberWidth, disableOrder: true },
    { title: 'Ações', field: 'actions', width: props.card ? '90px' : '120px', headerClass: 'justify-center', disableOrder: true },
  ]
})

// Methods
const loadItems = async (page = 1) => {
  const filters = [
    {
      column: 'idWarehouse',
      value: props.id,
      operator: '=',
    },
  ]

  if (selectedItem.value) {
    filters.push({
      column: 'idItem',
      value: selectedItem.value,
      operator: '=',
    })
  }

  const query = {
    params: {
      page,
      limit: control.value.limit,
      order: control.value.order,
      filters,
    },
  }

  loader.value = true
  const raw = await get(api, query)

  loader.value = false
  itemWarehouse.value = raw.data
  control.value.pages = Math.ceil(raw.total / control.value.limit)
  control.value.total = raw.total
}

const exportStock = async () => {
  const localFilters = [
    {
      column: 'idWarehouse',
      value: props.id,
      operator: '=',
    },
  ]

  if (selectedItem.value) {
    localFilters.push({
      column: 'idItem',
      value: selectedItem.value,
      operator: '=',
    })
  }

  // Monta o nome do arquivo com nome do depósito e data
  const today = new Date()
  const dateStr = today.toLocaleDateString('pt-BR').replace(/\//g, '-')
  const fileName = `items-do-deposito-${props.warehouseName || 'sem-nome'}-${dateStr}`

  const query = {
    params: {
      filters: localFilters,
      order: control.value.order,
      limit: 'Infinity',
    },
  }

  const raw = await get(api, query)

  const exportHeaders = stockHeaders.value
    .filter((header) => header.field !== 'actions')
    .map((header) => ({
      title: header.title,
      field: header.field,
    }))

  const exportItems = (raw.data || []).map((item) => ({
    ...item,
    avgPrice: formatMoneyS(item.avgPrice ?? 0),
    lastPrice: formatMoneyS(item.lastPrice ?? 0),
    total: formatMoneyS(item.total ?? 0),
  }))

  await getFile({}, fileName, exportHeaders, null, 'exporter/xlsx', exportItems)
}

const viewHistory = (item) => {
  router.push({
    name: 'itemApplicationHistory',
    params: { idItem: item.idItem, idWarehouse: props.id },
  })
}
//  Watchers
watch(
  () => control.value,
  () => {
    loadItems(control.value.page || 1)
  },
  { deep: true },
)

watch(
  () => props.id,
  (newVal) => {
    if (newVal) {
      loadItems()
    }
  },
)

watch(selectedItem, () => {
  loadItems(1)
})
// Life Cycle
onMounted(() => {
  loadItems()
})
</script>
